import { Key, Zap } from "lucide-react";
import { useCallback, useState } from "react";
import { Badge } from "@/components/ui/atoms/badge";
import { Button } from "@/components/ui/atoms/button";
import { Input } from "@/components/ui/atoms/input";
import { Label } from "@/components/ui/atoms/label";
import { Switch } from "@/components/ui/atoms/switch";
import type { ModelOption } from "@/hooks/use-models";
import { useModels } from "@/hooks/use-models";
import { groupModelsByProvider, PROVIDER_SLUGS } from "@/lib/model-provider-slugs";
import {
  getGlobalApiMode,
  getProviderEntry,
  type ProviderEntry,
  setGlobalApiMode,
  updateProviderConfig,
} from "@/lib/provider-config";
import { cn } from "@/lib/utils";
import { VisualsSetting } from "./visuals-setting";

export function AiSettings() {
  const models = useModels();
  const [apiMode, setApiMode] = useState(getGlobalApiMode);
  const modelGroups = groupModelsByProvider(models);

  const [, setVer] = useState(0);
  const forceUpdate = useCallback(() => setVer((v) => v + 1), []);

  const handleGlobalModeChange = useCallback((mode: "subscription" | "api-key") => {
    setGlobalApiMode(mode);
    setApiMode(mode);
  }, []);

  const handleToggleProvider = useCallback(
    (slug: string, enabled: boolean) => {
      updateProviderConfig(slug, { enabled });
      forceUpdate();
    },
    [forceUpdate],
  );

  const handleApiKeySave = useCallback(
    (slug: string, apiKey: string) => {
      updateProviderConfig(slug, { apiKey });
      forceUpdate();
    },
    [forceUpdate],
  );

  const handleBaseUrlSave = useCallback(
    (slug: string, baseUrl: string) => {
      updateProviderConfig(slug, { baseUrl });
      forceUpdate();
    },
    [forceUpdate],
  );

  const sortedSlugs = [...modelGroups.keys()].sort((a, b) => {
    if (a === "deepseek") return -1;
    if (b === "deepseek") return 1;
    if (a === "other") return 1;
    if (b === "other") return -1;
    return a.localeCompare(b);
  });

  if (models.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-sm text-muted-foreground">Loading models...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <div>
        <h2 className="text-lg font-semibold">AI Providers</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Choose how to connect to AI providers. Use a subscription or bring your own API keys.
        </p>
      </div>

      <div className="rounded-lg border p-4 space-y-3">
        <Label className="text-sm font-semibold">Connection mode</Label>
        <div className="flex gap-1 rounded-lg bg-muted p-1">
          <button
            type="button"
            onClick={() => handleGlobalModeChange("subscription")}
            className={cn(
              "flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              apiMode === "subscription"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Zap className="size-4" />
            Subscription
          </button>
          <button
            type="button"
            onClick={() => handleGlobalModeChange("api-key")}
            className={cn(
              "flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              apiMode === "api-key"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Key className="size-4" />
            Bring Your Own Keys
          </button>
        </div>

        {apiMode === "subscription" ? (
          <div className="rounded-lg border bg-muted/30 px-3 py-2.5">
            <p className="text-xs text-muted-foreground">
              Subscribe to access all AI providers. Subscription management coming soon.
            </p>
            <Button variant="outline" size="xs" className="mt-2" disabled>
              Subscribe
            </Button>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Enter your own API keys for each provider below.
          </p>
        )}
      </div>

      <VisualsSetting />

      {sortedSlugs.map((slug) => {
        const providerModels = modelGroups.get(slug) ?? [];
        const providerName = PROVIDER_SLUGS[slug] ?? slug;
        const providerConfig = getProviderEntry(slug);

        return (
          <ProviderCard
            key={slug}
            name={providerName}
            config={providerConfig}
            models={providerModels}
            apiMode={apiMode}
            onToggle={(enabled) => handleToggleProvider(slug, enabled)}
            onApiKeySave={(key) => handleApiKeySave(slug, key)}
            onBaseUrlSave={(url) => handleBaseUrlSave(slug, url)}
          />
        );
      })}
    </div>
  );
}

function ProviderCard({
  name,
  config,
  models,
  apiMode,
  onToggle,
  onApiKeySave,
  onBaseUrlSave,
}: {
  name: string;
  config: ProviderEntry;
  models: ModelOption[];
  apiMode: "subscription" | "api-key";
  onToggle: (enabled: boolean) => void;
  onApiKeySave: (key: string) => void;
  onBaseUrlSave: (url: string) => void;
}) {
  const [apiKey, setApiKey] = useState(config.apiKey ?? "");
  const [baseUrl, setBaseUrl] = useState(config.baseUrl ?? "");
  const [showKey, setShowKey] = useState(false);
  const [expanded, setExpanded] = useState(true);

  return (
    <div className={cn("rounded-lg border transition-colors", !config.enabled && "opacity-50")}>
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="size-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
            {name.charAt(0)}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm">{name}</span>
              <Badge variant="secondary" className="text-xs">
                {models.length} model{models.length !== 1 ? "s" : ""}
              </Badge>
            </div>
          </div>
        </div>
        <Switch checked={config.enabled} onCheckedChange={onToggle} size="sm" />
      </div>

      {config.enabled && apiMode === "api-key" && (
        <div className="border-t px-4 py-3 space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">API Key</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  type={showKey ? "text" : "password"}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="sk-..."
                  className="h-8 text-xs font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowKey(!showKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground"
                >
                  {showKey ? "Hide" : "Show"}
                </button>
              </div>
              <Button size="xs" onClick={() => onApiKeySave(apiKey)} disabled={!apiKey}>
                Save
              </Button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Base URL (optional)</Label>
            <div className="flex gap-2">
              <Input
                type="url"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://api.openai.com/v1"
                className="h-8 text-xs font-mono"
              />
              <Button size="xs" onClick={() => onBaseUrlSave(baseUrl)} disabled={!baseUrl}>
                Save
              </Button>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <span className={cn("transition-transform", expanded && "rotate-90")}>&#9656;</span>
            {models.length} available models
          </button>

          {expanded && (
            <div className="space-y-1 pl-4 border-l-2 border-muted ml-2">
              {models.map((model) => (
                <div key={model.id} className="flex items-center gap-2 py-1">
                  <span className="text-xs font-mono text-muted-foreground truncate">
                    {model.id}
                  </span>
                  <span className="text-xs text-muted-foreground truncate">
                    {model.description}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
